import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '../datos/db.js'
import { encolar } from '../datos/sync.js'
import { fechaLarga, hora, guaranies } from '../datos/formato.js'
import { FormularioCargo, FormularioPago } from '../comunes/FormulariosCobro.js'
import { Odontograma } from '../paciente/Odontograma.js'
import { Anamnesis } from '../paciente/Anamnesis.js'
import { AnamnesisOdontologica } from '../paciente/AnamnesisOdontologica.js'
import { edadEn, esMenorDeEdad } from '@practiclinic/core'

interface FichaExtendida {
  cliente: {
    id: string
    nombre: string
    doc: string | null
    ruc: string | null
    telefono: string | null
    coberturaId: string | null
    waOptin: boolean
    fechaNacimiento: string | null
    datos: Record<string, unknown>
  }
  historial: {
    id: string
    estado: string
    origen: string
    notas: string | null
    inicio: string | null
    fin: string | null
    anamnesisOdontologica: Record<string, unknown> | null
  }[]
}


const API = '/api'

/**
 * Pantalla de atención: cita → paciente → cargo → cobro (T15c).
 *
 * Conserva citaId y clienteId de punta a punta. Se abre desde DetalleCita
 * (recepción) y MiDia (profesional) con la misma cita.
 *
 * La única red permitida es `GET /clientes/:id/ficha` (excepción declarada
 * en Fichas.tsx). Todo lo demás sale de Dexie.
 */
export function Atencion({
  citaId,
  onCerrar,
  onCobrar,
  destinoPago,
}: {
  citaId: string
  onCerrar: () => void
  /**
   * Sólo la tiene recepción: es la que cierra la atención llevando la cita al
   * mostrador. El profesional no la recibe —no tiene pantalla de cobros— y para
   * él el cierre sigue siendo cerrar.
   */
  onCobrar?: ((citaId: string) => void) | undefined
  /**
   * Dónde entra la plata que se cobre acá. Lo decide `App` por rol: el
   * profesional que cobra en su sillón manda la plata directo a él, no al
   * cajón; recepción la manda al mostrador (T25).
   */
  destinoPago?: 'caja' | 'profesional'
}) {
  const [ficha, setFicha] = useState<FichaExtendida | null>(null)
  const [errorFicha, setErrorFicha] = useState<string | null>(null)
  const [cargandoFicha, setCargandoFicha] = useState(false)
  const [cerrando, setCerrando] = useState(false)
  const [alertasMedicas, setAlertasMedicas] = useState<{ nivel: 'roja' | 'amarilla'; codigo: string; texto: string }[]>([])
  const [servicioSugeridoId, setServicioSugeridoId] = useState<string | null>(null)
  const [piezaEstadoSugeridoId, setPiezaEstadoSugeridoId] = useState<string | null>(null)
  const [evolucion, setEvolucion] = useState('')
  const [evolucionGuardada, setEvolucionGuardada] = useState(false)
  const [guardandoEvolucion, setGuardandoEvolucion] = useState(false)

  const detalle = useLiveQuery(async () => {
    const cita = await db.cita.get(citaId)
    if (!cita) return null
    const cliente = await db.cliente.get(cita.cliente_id)
    const tramos = await db.cita_tramo
      .where('cita_id')
      .equals(citaId)
      .sortBy('orden')
    const reservas = tramos.length
      ? await db.reserva_recurso
          .where('cita_tramo_id')
          .anyOf(tramos.map((t) => t.id))
          .toArray()
      : []
    const recursos = await db.recurso.bulkGet([
      ...new Set(reservas.filter((r) => r.activa).map((r) => r.recurso_id)),
    ])
    const cargos = await db.cargo.where('cita_id').equals(citaId).toArray()
    const pagos = await db.pago
      .where('cargo_id')
      .anyOf(cargos.map((c) => c.id))
      .toArray()
    const servicios = await db.servicio.toArray()
    return {
      cita,
      cliente,
      tramos,
      recursos: recursos.filter((r) => r !== undefined),
      cargos,
      pagos,
      servicios,
    }
  }, [citaId])

  // Ficha extendida: la única red en todo el módulo.
  useEffect(() => {
    if (!detalle?.cliente) return
    setCargandoFicha(true)
    setFicha(null)
    setErrorFicha(null)
    fetch(`${API}/clientes/${detalle.cliente.id}/ficha`, { credentials: 'include' })
      .then((r) => {
        if (!r.ok) throw new Error('No pudimos traer la ficha completa')
        return r.json()
      })
      .then((f) => setFicha(f as FichaExtendida))
      .catch((e: unknown) =>
        setErrorFicha(e instanceof Error ? e.message : 'Algo salió mal'),
      )
      .finally(() => setCargandoFicha(false))
  }, [detalle?.cliente])

  useEffect(() => {
    if (!detalle?.cliente) return
    void fetch(`${API}/clinico/${detalle.cliente.id}/notas`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error()))
      .then((r: { notas: { citaId: string | null; texto: string }[] }) => {
        const nota = r.notas.find((n) => n.citaId === citaId)
        setEvolucion(nota?.texto ?? '')
        setEvolucionGuardada(Boolean(nota))
      })
      .catch(() => setEvolucionGuardada(false))
  }, [detalle?.cliente, citaId])

  useEffect(() => {
    if (!detalle?.cliente) return
    void fetch(`${API}/clinico/${detalle.cliente.id}/anamnesis`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error()))
      .then((r: { actual: { alertas: typeof alertasMedicas } | null }) => setAlertasMedicas(r.actual?.alertas ?? []))
      .catch(() => setAlertasMedicas([]))
  }, [detalle?.cliente])

  // Poner la cita en atención si está confirmada.
  useEffect(() => {
    if (detalle?.cita.estado === 'confirmada') {
      void db.cita.update(citaId, { estado: 'en_atencion' })
      void encolar('cambiar_estado', { citaId, estado: 'en_atencion' })
    }
  }, [detalle?.cita.estado, citaId])

  async function cambiarEstado(
    estado: 'en_atencion' | 'atendida' | 'no_asistio',
  ) {
    await db.cita.update(citaId, { estado })
    await encolar('cambiar_estado', { citaId, estado })
  }

  async function cerrarAtencion() {
    if (cerrando) return
    if (!detalle) return
    if (!evolucionGuardada) {
      document.getElementById('evolucion-atencion')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      window.alert('Guardá la evolución clínica antes de finalizar la atención.')
      return
    }

    // Con saldo y con mostrador no hay nada que preguntar: cerrar y cobrar son
    // el mismo gesto, y el paso siguiente es la fila de esta cita en Cobros. El
    // `confirm` queda sólo para el profesional, que no tiene a dónde ir con la
    // deuda y necesita enterarse antes de dar la atención por terminada.
    const alMostrador = saldo > 0 && onCobrar !== undefined

    if (saldo > 0 && !alMostrador) {
      const ok = window.confirm(
        `Queda un saldo de ${guaranies(saldo)}. ¿Seguís queriendo cerrar la atención? ` +
          'Podés cobrar después desde Cobros.',
      )
      if (!ok) return
    }

    setCerrando(true)
    await cambiarEstado('atendida')
    if (alMostrador && onCobrar) onCobrar(citaId)
    else onCerrar()
  }

  if (detalle === undefined) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
        <p className="text-sm text-slate-500">Cargando…</p>
      </div>
    )
  }

  if (detalle === null) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-sm text-slate-600">La cita ya no está disponible.</p>
          <button
            type="button"
            onClick={onCerrar}
            className="mt-4 text-sm text-teal-700 underline"
          >
            Volver
          </button>
        </div>
      </div>
    )
  }

  const primero = detalle.tramos[0]
  const ultimo = detalle.tramos.at(-1)
  const pagadoPorCargo = pagadoMap(detalle.pagos)
  const saldo = calcularSaldo(detalle.cargos, detalle.pagos)

  return (
    // z-50, como el panel lateral: la barra de navegación es `sticky z-40` y
    // con z-30 le tapaba el encabezado, que es justo lo que tiene que quedar
    // siempre a la vista mientras dura la atención.
    <div className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-white">
      {/* Encabezado permanente */}
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white px-4 py-4">
        <div className="mb-1 flex items-center justify-between">
          <h1 className="text-base font-semibold text-slate-900">
            {detalle.cliente?.nombre ?? 'Sin paciente'}
          </h1>
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
            {etiquetaEstado(detalle.cita.estado)}
          </span>
        </div>
        <p className="text-sm text-slate-500">
          {primero
            ? `${fechaLarga(primero.inicio)} · ${hora(primero.inicio)}${ultimo ? `–${hora(ultimo.fin)}` : ''}`
            : 'Sin horario'}
        </p>
        <p className="text-sm text-slate-500">
          {detalle.recursos.length
            ? detalle.recursos.map((r) => r.nombre).join(' · ')
            : 'Sin profesional/asignado'}
        </p>
      </header>

      <div className="flex-1 space-y-6 p-4">
        {/* Datos del paciente */}
        <section>
          <h2 className="mb-2 text-[13px] font-medium text-slate-600">
            Datos del paciente
          </h2>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <Dato etiqueta="Documento">{detalle.cliente?.doc ?? 'Sin documento'}</Dato>
              <Dato etiqueta="Teléfono">{detalle.cliente?.telefono ?? 'Sin teléfono'}</Dato>
              {ficha?.cliente.fechaNacimiento && edadEn(ficha.cliente.fechaNacimiento, new Date()) !== null && (
                <Dato etiqueta="Edad">{`${edadEn(ficha.cliente.fechaNacimiento, new Date())} años`}</Dato>
              )}
              {Boolean(ficha?.cliente.datos['sexo']) && <Dato etiqueta="Sexo">{String(ficha?.cliente.datos['sexo'])}</Dato>}
              <Dato etiqueta="Cobertura">{String(ficha?.cliente.datos['seguroMedico'] || 'Particular / sin seguro')}</Dato>
              {detalle.cita.notas && (
                <Dato etiqueta="Notas de la cita">{detalle.cita.notas}</Dato>
              )}
            </dl>
            {ficha && esMenorDeEdad(ficha.cliente.fechaNacimiento, new Date()) && <p className="mt-3 rounded-lg bg-rose-600 p-3 text-sm font-bold text-white">PACIENTE MENOR DE EDAD</p>}
            {alertasMedicas.map((alerta) => <p key={alerta.codigo} className={`mt-2 rounded-lg p-3 text-sm font-bold ${alerta.nivel === 'roja' ? 'bg-rose-100 text-rose-900' : 'bg-amber-100 text-amber-900'}`}>{alerta.texto}</p>)}

            {/* Ficha extendida */}
            {cargandoFicha && (
              <p className="mt-3 text-xs text-slate-400">
                Cargando ficha extendida…
              </p>
            )}
            {errorFicha && (
              <p className="mt-3 text-xs text-amber-700">
                No se pudo cargar la ficha completa: {errorFicha}
              </p>
            )}
          </div>
        </section>

        <AnamnesisOdontologica clienteId={detalle.cita.cliente_id} citaId={citaId} />

        {ficha && <Anamnesis clienteId={detalle.cita.cliente_id}
          esMenor={esMenorDeEdad(ficha.cliente.fechaNacimiento, new Date())} compacta />}

        {/* Historial */}
        {ficha && ficha.historial.some((h) => h.id !== citaId && h.estado === 'atendida') && (
          <section id="historial-atencion" className="scroll-mt-24">
            <h2 className="mb-2 text-[13px] font-medium text-slate-600">
              Consultas anteriores
            </h2>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-left text-sm"><thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Fecha</th><th className="px-4 py-3">Tratamiento / motivo</th><th className="px-4 py-3">Estado</th></tr></thead><tbody className="divide-y divide-slate-100">
                {ficha.historial.filter((h) => h.id !== citaId && h.estado === 'atendida').slice(0, 3).map((h) => <tr key={h.id}><td className="whitespace-nowrap px-4 py-3">{h.inicio ? `${fechaLarga(h.inicio)} · ${hora(h.inicio)}` : 'Sin fecha'}</td><td className="px-4 py-3">{String(h.anamnesisOdontologica?.['motivoConsulta'] ?? h.notas ?? 'Sin detalle registrado')}</td><td className="px-4 py-3 text-slate-500">{etiquetaEstado(h.estado)}</td></tr>)}
              </tbody></table>
            </div>
          </section>
        )}

        <section id="odontograma-atencion" className="scroll-mt-24">
          <h2 className="mb-2 text-[13px] font-medium text-slate-600">Odontograma</h2>
          <Odontograma clienteId={detalle.cita.cliente_id} citaId={citaId}
            servicios={detalle.servicios} onSugerirCargo={(sugerencia) => { setServicioSugeridoId(sugerencia.servicioId); setPiezaEstadoSugeridoId(sugerencia.piezaEstadoId) }} />
        </section>

        <section id="evolucion-atencion" className="scroll-mt-24 rounded-xl border border-teal-200 bg-teal-50 p-4">
          <div className="flex items-center justify-between gap-3"><div><h2 className="font-semibold text-slate-900">Evolución clínica</h2><p className="text-xs text-slate-500">Qué se diagnosticó, qué se realizó y qué indicaciones se dieron.</p></div>{evolucionGuardada && <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-800">✓ Guardada</span>}</div>
          <textarea rows={4} value={evolucion} onChange={(e) => { setEvolucion(e.target.value); setEvolucionGuardada(false) }} placeholder="Ej.: Se realizó restauración de pieza 46, anestesia local sin complicaciones. Indicación de ibuprofeno y control en 7 días." className="mt-3 w-full rounded-lg border border-teal-300 bg-white p-3 text-sm" />
          <button type="button" disabled={guardandoEvolucion || evolucion.trim().length < 3} onClick={async () => {
            setGuardandoEvolucion(true)
            try {
              const r = await fetch(`${API}/clinico/${detalle.cita.cliente_id}/notas`, { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ texto: evolucion.trim(), citaId }) })
              if (r.ok) setEvolucionGuardada(true)
            } finally { setGuardandoEvolucion(false) }
          }} className="mt-3 rounded-lg bg-teal-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{guardandoEvolucion ? 'Guardando…' : 'Guardar evolución'}</button>
        </section>

        {/* Cargos de esta cita */}
        <section>
          <h2 className="mb-2 text-[13px] font-medium text-slate-600">
            Cargos y cobros
          </h2>
          {detalle.cargos.length > 0 ? (
            <ul className="mb-3 divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white">
              {detalle.cargos.map((cargo) => {
                const pagado = pagadoPorCargo.get(cargo.id) ?? 0
                const saldoCargo = cargo.monto - pagado
                const nombre =
                  detalle.servicios.find((s) => s.id === cargo.servicio_id)
                    ?.nombre ?? 'Servicio'
                return (
                  <li key={cargo.id} className="px-4 py-3">
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm text-slate-700">{nombre}</span>
                      <span className="text-sm tabular-nums text-slate-500">
                        {guaranies(cargo.monto)}
                      </span>
                    </div>
                    {saldoCargo > 0 && (
                      <p className="mt-1 text-xs text-amber-700">
                        Saldo: {guaranies(saldoCargo)}
                      </p>
                    )}
                  </li>
                )
              })}
            </ul>
          ) : (
            <p className="mb-3 text-sm text-slate-400">
              No hay cargos registrados para esta cita.
            </p>
          )}

          <FormularioCargo
            citaId={citaId}
            recursosCita={detalle.recursos.map((r) => r.id)}
            servicios={detalle.servicios}
            onListo={() => {}}
            servicioSugeridoId={servicioSugeridoId}
            piezaEstadoSugeridoId={piezaEstadoSugeridoId}
          />

          {/* Pagos pendientes de esta cita */}
          {detalle.cargos.some((cargo) => {
            const pagado = pagadoPorCargo.get(cargo.id) ?? 0
            return cargo.monto - pagado > 0
          }) && (
            <div className="mt-4 space-y-3">
              <h3 className="text-[13px] font-medium text-slate-600">
                Registrar pago
              </h3>
              {detalle.cargos.map((cargo) => {
                const pagado = pagadoPorCargo.get(cargo.id) ?? 0
                const saldoCargo = cargo.monto - pagado
                if (saldoCargo <= 0) return null
                const nombre =
                  detalle.servicios.find((s) => s.id === cargo.servicio_id)
                    ?.nombre ?? 'Servicio'
                const conCobertura =
                  (detalle.cliente?.cobertura_id ?? null) !== null
                return (
                  <div
                    key={`pago-${cargo.id}`}
                    className="rounded-xl border border-slate-200 bg-white p-3"
                  >
                    <p className="mb-2 text-sm text-slate-700">
                      {nombre} — saldo {guaranies(saldoCargo)}
                    </p>
                    <FormularioPago
                      cargoId={cargo.id}
                      saldo={saldoCargo}
                      sugerirTercero={conCobertura}
                      {...(destinoPago ? { destinoInicial: destinoPago } : {})}
                    />
                  </div>
                )
              })}
            </div>
          )}
        </section>
      </div>

      {/* Pie fijo */}
      <footer className="sticky bottom-0 border-t border-slate-200 bg-white px-4 py-3">
        <button
          type="button"
          onClick={() => void cerrarAtencion()}
          disabled={cerrando}
          className="min-h-11 w-full rounded-lg bg-teal-700 px-4 text-sm font-medium text-white disabled:opacity-50"
        >
          {saldo > 0 && onCobrar
            ? `Cerrar y cobrar — ${guaranies(saldo)}`
            : 'Cerrar atención'}
        </button>
      </footer>
    </div>
  )
}

function Dato({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="txt-meta">{etiqueta}</dt>
      <dd className="mt-0.5 text-slate-800">{children}</dd>
    </div>
  )
}

function etiquetaEstado(estado: string): string {
  const etiquetas: Record<string, string> = {
    pendiente: 'Pendiente',
    confirmada: 'Confirmada',
    en_atencion: 'En atención',
    atendida: 'Atendida',
    facturada: 'Facturada',
    cancelada: 'Cancelada',
    no_asistio: 'No asistió',
    pendiente_reprogramar: 'Pendiente de reprogramar',
  }
  return etiquetas[estado] ?? estado
}

function pagadoMap(pagos: { cargo_id: string; monto: number }[]): Map<string, number> {
  const mapa = new Map<string, number>()
  for (const p of pagos) {
    mapa.set(p.cargo_id, (mapa.get(p.cargo_id) ?? 0) + p.monto)
  }
  return mapa
}

function calcularSaldo(
  cargos: { monto: number }[],
  pagos: { cargo_id: string; monto: number }[],
): number {
  const total = cargos.reduce((a, c) => a + c.monto, 0)
  const cobrado = pagos.reduce((a, p) => a + p.monto, 0)
  return total - cobrado
}

// ────────────────────────────────────────────────────────
// Formularios copiados de Cobros.tsx para cumplir la
// restricción de no tocar archivos fuera del alcance.
// Ver informe para el análisis de extracción.
// ────────────────────────────────────────────────────────
